/**
 * contactTypeBadge — the colour a contact's Type badge gets, and where it
 * comes from.
 *
 * The contacts table used to put a per-row ICON in the Type column, which had
 * two problems: an icon cannot carry seventy distinct type labels, and it said
 * nothing about how the types RELATE. The type list is a two-level tree —
 * a handful of parents ("Individual", "Business", "Government", "Legal
 * Professional" …) with the real types hanging off them — so the useful
 * signal is the PARENT: at a glance, is this row a person, a company, a public
 * body, another firm.
 *
 * So the badge is coloured by the parent, and labelled by the leaf. Two rows
 * with different types under the same parent read as the same family.
 *
 * WHERE THE PARENT COMES FROM. `GET /clients` sends `parent_type_code` /
 * `parent_type_label` per contact (backend branch `feature/final-revamp-backend`).
 * Until that ships, `parentIndexFromTypeTree` derives the same thing from the
 * `/dropdown/client-types` list the page already fetches — same tree, same
 * answer — so the column is correct either way.
 */

import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';

/**
 * The palette, in assignment order.
 *
 * Deliberately NOT the status colours: green/red/yellow mean active, failed
 * and pending everywhere else in the app, and a type is not a status. These
 * are the neutral hues left over — a family marker, not a judgement.
 */
export const CONTACT_TYPE_COLORS: BadgeColor[] = [
  'indigo',
  'teal',
  'violet',
  'sky',
  'amber',
  'pink',
  'cyan',
  'orange',
];

/**
 * A stable index for a parent key.
 *
 * Hashed rather than enumerated because the parent list is the organisation's,
 * not ours — a firm can add one — and a lookup table would hand every unknown
 * parent the same grey. The hash is order-independent and deterministic, so a
 * parent keeps its colour across sessions and across pages.
 */
function hashIndex(key: string, buckets: number): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 100003;
  }
  return hash % buckets;
}

export interface ContactTypeBadge {
  label: string;
  color: BadgeColor;
}

export interface ContactTypeInput {
  /** `type_label` from the row, else the label resolved from the type map. */
  typeLabel?: string | null;
  /** `parent_type_code` — the grouping key. Falls back to the parent label. */
  parentCode?: string | null;
  parentLabel?: string | null;
  /** `entity_kind` — the last-resort grouping when no parent is known. */
  entityKind?: string | null;
}

/**
 * The badge for one contact.
 *
 * Falls through in order of how much the row actually tells us: the parent
 * code, the parent label, the entity kind, and finally the leaf label itself —
 * so a contact whose type carries no parent still lands on a consistent colour
 * rather than on grey.
 */
export function contactTypeBadge(
  input: ContactTypeInput,
  fallbackLabel: string,
): ContactTypeBadge {
  const label = (input.typeLabel || '').trim() || fallbackLabel;
  const key = (
    input.parentCode ||
    input.parentLabel ||
    input.entityKind ||
    label
  )
    .toString()
    .trim()
    .toLowerCase();

  if (!key) return { label, color: 'gray' };
  return { label, color: CONTACT_TYPE_COLORS[hashIndex(key, CONTACT_TYPE_COLORS.length)] };
}

/**
 * type_id → its parent's label, read off the flat `/dropdown/client-types`
 * list. A root type is its own parent, so "Business" as a leaf and "Business"
 * as a family colour the same.
 *
 * This is the pre-backend fallback: once `parent_type_code` arrives on the row
 * it wins, because the backend knows the tree it built.
 */
export function parentLabelsByTypeId(flatTypes: any[]): Record<number, string> {
  const byId = new Map<number, any>();
  (flatTypes || []).forEach((item: any) => {
    const id = typeof item?.id === 'string' ? parseInt(item.id, 10) : item?.id;
    if (id !== undefined && id !== null && !Number.isNaN(id)) byId.set(id, item);
  });

  const result: Record<number, string> = {};
  byId.forEach((item, id) => {
    const parentId =
      typeof item?.parent_id === 'string' ? parseInt(item.parent_id, 10) : item?.parent_id;
    const parent = parentId !== null && parentId !== undefined ? byId.get(parentId) : null;
    result[id] = (parent?.label || item?.label || '') as string;
  });
  return result;
}
